"use client"

import Button from "components/Buttons/Button"
import gsap from "gsap/all"
import IMG from "images/home/forBrokers/ForBrokers.png"
import MobileIMG from "images/home/forBrokers/ForBrokersMobile.png"
import { ScreenContext } from "library/ScreenContext"
import StaticImage from "library/StaticImage"
import { css, f, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { useContext, useRef } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

export default function ForBrokers() {
	const { mobile, tablet } = useContext(ScreenContext)
	const imgAltText =
		"A person wearing a light pink hijab and brown top holding a tablet against a neutral beige background."
	const titleText = "Powering Broker Success with Better Healthcare"
	const wrapperRef = useRef<HTMLDivElement | null>(null)

	useAnimation(() => {
		if (!wrapperRef.current || mobile) return

		gsap.to(wrapperRef.current, {
			scrollTrigger: {
				trigger: wrapperRef.current,
				start: "top 80%",
				end: "top 50%",
				scrub: 0.5,
			},
			ease: "power2.inOut",
			borderRadius: "20px 20px 0 0",
		})
	}, [mobile])

	return (
		<Wrapper aria-labelledby="brokers-section-title" ref={wrapperRef}>
			<Inner>
				<InnerContent>
					<TitleTablet id={tablet ? "brokers-section-title" : undefined}>
						{titleText}
					</TitleTablet>
					<Content>
						<Image
							src={IMG}
							width={tablet ? 321 : 525}
							height={tablet ? 300 : 490}
							alt={imgAltText}
							loading="lazy"
						/>
						<MobileImage
							src={MobileIMG}
							width={327}
							height={327}
							alt={imgAltText}
							loading="lazy"
						/>
						<TextContent>
							<Title id={!tablet ? "brokers-section-title" : undefined}>
								{titleText}
							</Title>
							<Text>
								Arlo gives brokers a faster, sharper way to compete. Instant
								quotes, live plan analytics, and predictable renewals come
								bundled with white-glove support—so you win more groups and keep
								them, minus the back-and-forth.
							</Text>
							<Button
								href={links.brokers}
								size={"large"}
								aria-label="Learn more about Arlo for brokers"
							>
								Visit Brokerage Page
							</Button>
						</TextContent>
					</Content>
				</InnerContent>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		display: grid;
		place-items: center;
		padding: 0;
		background: ${colors.greenMain};
		border-radius: 0;
		position: relative;
		z-index: 2;
	`),

	...f.mobile(css`
		background: ${colors.greenMain};
		padding: 2px 0 0;
		border-radius: 0;
		margin-top: -1px;
	`),
})

const Inner = styled("div", {
	...f.responsive(css`
		margin: 0 auto;
		width: 100%;
		background: ${colors.offWhite};
		padding: 0;
		border-radius: 20px 20px 0 0;
	`),
	...f.tablet(css`
		width: 1024px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 81px;
	`),
	...f.mobile(css`
		width: 375px;
		border-radius: 16px 16px 0 0;
	`),
})

const InnerContent = styled("div", {
	...f.responsive(css`
		margin: 0 auto;
		width: 1440px;
		padding: 146px 114px 130px 116px;
		border-radius: 20px 20px 0 0;
	`),
	...f.tablet(css`
		width: 1024px;
		padding: 236px 138px 220px 145px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 81px;
	`),
	...f.mobile(css`
		width: 375px;
		padding: 68px 24px 44px;
		border-radius: 16px 16px 0 0;
	`),
})

const TitleTablet = styled("h2", {
	...f.responsive(css`
		display: none;
	`),
	...f.tablet(css`
		color: ${colors.black100};
		${textStyles.h3};
		display: block;
		text-align: center;
		width: 605px;
	`),
})

const Content = styled("div", {
	...f.responsive(css`
		display: flex;
		gap: 80px;
		align-items: center;
	`),
	...f.tablet(css`
		gap: 48px;
	`),
	...f.mobile(css`
		flex-direction: column;
		gap: 23px;
	`),
})

const Image = styled(StaticImage, {
	...f.responsive(css`
		width: 525px;
		border-radius: 20px;
	`),
	...f.tablet(css`
		width: 321px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const MobileImage = styled(StaticImage, {
	...f.responsive(css`
		display: none;
	`),
	...f.mobile(css`
		display: block;
		width: 327px;
		border-radius: 20px;
	`),
})

const TextContent = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		width: 605px;
	`),
	...f.tablet(css`
		width: 372px;
	`),
	...f.mobile(css`
		width: 327px;
	`),
})

const Title = styled("h2", {
	...f.responsive(css`
		color: ${colors.black100};
		${textStyles.h4};
	`),
	...f.tablet(css`
		display: none;
	`),
	...f.mobile(css`
		${textStyles.h6};
	`),
})

const Text = styled("p", {
	...f.responsive(css`
		color: ${colors.green400};
		${textStyles.bodyR};
		width: 411px;
		margin: 36px 0 44px;
	`),
	...f.tablet(css`
		width: 100%;
		margin: 0 0 44px;
	`),
	...f.mobile(css`
		width: 327px;
		${textStyles.bodyS};
	`),
})
